import { useEffect, useState } from 'react';
import { useI18n } from '../i18n';
import './AchievementToast.css';

const ACHIEVEMENT_ICONS: Record<string, string> = {
  first_win: '🏆',
  ten_wins: '🔟',
  fifty_wins: '⭐',
  speed_demon: '⚡',
  perfectionist: '💎',
  streak_3: '🔥',
  streak_7: '📅',
  streak_30: '🗓️',
  all_difficulties: '🎯',
  century: '💯',
  daily_solver: '📰',
  social_player: '👥',
};

interface Props {
  achievementIds: string[];
  onDone?: () => void;
}

export function AchievementToast({ achievementIds, onDone }: Props) {
  const { t } = useI18n();
  const [queue, setQueue] = useState<string[]>([]);
  const [current, setCurrent] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (achievementIds.length > 0) {
      setQueue(prev => [...prev, ...achievementIds]);
    }
  }, [achievementIds]);

  useEffect(() => {
    if (!current && queue.length > 0) {
      const [next, ...rest] = queue;
      setQueue(rest);
      setCurrent(next);
      setVisible(true);
      const hideTimer = setTimeout(() => setVisible(false), 3000);
      const clearTimer = setTimeout(() => {
        setCurrent(null);
        if (rest.length === 0) onDone?.();
      }, 3500);
      return () => { clearTimeout(hideTimer); clearTimeout(clearTimer); };
    }
  }, [current, queue]);

  if (!current) return null;

  const icon = ACHIEVEMENT_ICONS[current] ?? '🏅';
  const key = `ach_${current}` as keyof typeof t.profile;
  const name = (t.profile as unknown as Record<string, string>)[key] ?? current;

  return (
    <div className={`achievement-toast ${visible ? 'visible' : 'hiding'}`}>
      <span className="ach-toast-icon">{icon}</span>
      <div className="ach-toast-text">
        <span className="ach-toast-label">{t.profile.achievementUnlocked}</span>
        <span className="ach-toast-name">{name}</span>
      </div>
    </div>
  );
}
